import toast from 'react-hot-toast';
import type { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { z } from 'zod';

import { APIEndPoints, Request } from '../apis';
import type { TUser } from '../components/types';
import { MINUTE } from '../common/utils/constants';

export type UsersStoreState = {
  merchantUsers: ReadonlyArray<TUser>;
  platformUsers: ReadonlyArray<TUser>;
  merchantUser: TUser | null;
  platformUser: TUser | null;
  // Mutations
  setMerchantUsers: (merchantUsers: ReadonlyArray<TUser>) => void;
  setPlatformUsers: (platformUsers: ReadonlyArray<TUser>) => void;
  setMerchantUser: (merchantUser: TUser) => void;
  setPlatformUser: (platformUser: TUser) => void;
  clearState: () => void;
};

const STARTING_STATE = {
  merchantUsers: [],
  platformUsers: [],
  merchantUser: null,
  platformUser: null
} as const;

export const useUsersStore = create<UsersStoreState>((set) => ({
  ...STARTING_STATE,
  setMerchantUsers: (merchantUsers) => {
    set({ merchantUsers });
  },
  setPlatformUsers: (platformUsers) => {
    set({ platformUsers });
  },
  setMerchantUser: (merchantUser) => {
    set({ merchantUser });
  },
  setPlatformUser: (platformUser) => {
    set({ platformUser });
  },
  clearState: () => {
    set({ ...STARTING_STATE });
  }
}));

export const clearUsersStore = () => {
  useUsersStore.setState({ ...STARTING_STATE });
};

export const getPlatformUser = ({
  userId,
  setPlatformUser,
  query
}: {
  userId: string;
  setPlatformUser: UsersStoreState['setPlatformUser'];
  query: typeof useQuery;
}) => {
  return query(
    ['userId', userId],
    () =>
      Request.get<TUser>({
        path: `${APIEndPoints.usersPlatform}/${userId}?access_token`,
        schema: z.any()
      }),
    {
      onSuccess: (data) => {
        setPlatformUser(data);
      },
      onError: (error) => {
        toast.error(
          'Something went wrong while trying to load this page. Please refresh and try again.'
        );
      },
      staleTime: 30 * MINUTE,
      refetchOnMount: 'always' as const
    }
  );
};

export const getLoggedInUser = ({
  userId,
  merchantId,
  setMerchantUser,
  setMerchantUsers,
  query
}: {
  userId: string;
  merchantId: string;
  setMerchantUser: UsersStoreState['setMerchantUser'];
  setMerchantUsers: UsersStoreState['setMerchantUsers'];
  query: typeof useQuery;
}) => {
  return query(
    ['userId', userId !== 'PLATFORM' && userId !== undefined],
    () =>
      Request.get<TUser>({
        path: `${APIEndPoints.loggedInUser}`,
        schema: z.any()
      }),
    {
      onSuccess: (data) => {
        setMerchantUser(data);
        setMerchantUsers([data]);
      },
      onError: (error) => {
        toast.error(
          'Something went wrong while trying to load this page. Please refresh and try again.'
        );
      },
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 2000
    }
  );
};

export const getMerchantUser = ({
  userId,
  merchantId,
  setMerchantUser,
  query
}: {
  userId: string;
  merchantId: string;
  setMerchantUser: UsersStoreState['setMerchantUser'];
  query: typeof useQuery;
}) => {
  return query(
    ['userId', userId],
    () =>
      Request.get<TUser>({
        path: `${APIEndPoints.usersMerchant}/${merchantId}/${userId}?access_token`,
        schema: z.any()
      }),
    {
      onSuccess: (data) => {
        setMerchantUser(data);
      },
      onError: (error) => {
        toast.error(
          'Something went wrong while trying to load this page. Please refresh and try again.'
        );
      },
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 2000
    }
  );
};

export const getPlatformUsers = ({
  setPlatformUsers,
  query
}: {
  setPlatformUsers: UsersStoreState['setPlatformUsers'];
  query: typeof useQuery;
}) => {
  return query(
    ['platformUsers'],
    () =>
      Request.get<Array<TUser>>({
        path: `${APIEndPoints.usersPlatform}?access_token`,
        schema: z.any()
      }),
    {
      onSuccess: (data) => {
        setPlatformUsers(data);
      },
      onError: (error) => {
        toast.error(
          'Something went wrong while trying to load this page. Please refresh and try again.'
        );
      },
      refetchInterval: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 2000
    }
  );
};

// @TODO - should this be scoped to a single salesperson (on the backend) if role is tenant user?
export const getMerchantUsers = ({
  // @TODO - this will go away once auth/sessions are automatically forwarded to API
  merchantId,
  setMerchantUsers,
  query,
  enabled = true
}: {
  merchantId: string;
  setMerchantUsers: UsersStoreState['setMerchantUsers'];
  query: typeof useQuery;
  enabled: boolean;
}) => {
  return query(
    ['merchantUsers'],
    () =>
      Request.get<Array<TUser>>({
        path: `${APIEndPoints.usersMerchant}/${merchantId}?access_token`,
        schema: z.any(),
        additionalHeaders: {
          merchant: merchantId
        }
      }),
    {
      onSuccess: (data) => {
        setMerchantUsers(data);
      },
      onError: (error) => {
        toast.error(
          'Something went wrong while trying to load this page. Please refresh and try again.'
        );
      },
      enabled,
      refetchInterval: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 2000
    }
  );
};
